import * as React from 'react';
import { Loading } from 'vet-bones/design-system/components';
import { alphaSort } from 'vet-bones/utils';

import { DataTable, DataTableColumn } from 'src/ui/components/DataTable';
import {
  LAUNCHPAD_ENDPOINT_TABLE_COLUMN_SORT,
  LAUNCHPAD_ENDPOINT_TABLE_SEARCH,
} from 'src/ui/constants/testIds';
import { LaunchpadEndpointRow } from 'src/ui/containers/endpoints/LaunchpadEndpointRow';
import { getEndpointAuthType } from 'src/ui/containers/endpoints/utils/getAuthType';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import { Connection, useSavedConnectionsQuery } from 'src/ui/graph/types';
import * as copy from 'src/ui/templates/copy';

const COLUMNS: Array<DataTableColumn<Connection>> = [
  {
    key: 'name',
    className: 'organization-table-row-endpoint-name',
    header: copy.components.endpoints.endpointTable.columns.name,
    sortByAlpha: (item: Connection) => item.name,
    sortTestId: LAUNCHPAD_ENDPOINT_TABLE_COLUMN_SORT('name'),
  },
  {
    key: 'url',
    className: 'organization-table-row-endpoint-url',
    header: copy.components.endpoints.endpointTable.columns.url,
    sortByAlpha: (item: Connection) => item.endpoint,
    sortTestId: LAUNCHPAD_ENDPOINT_TABLE_COLUMN_SORT('url'),
  },
  {
    key: 'auth',
    className: 'organization-table-row-endpoint-auth',
    header: copy.components.endpoints.endpointTable.columns.auth,
    sortByAlpha: (item: Connection) => getEndpointAuthType(item),
    sortTestId: LAUNCHPAD_ENDPOINT_TABLE_COLUMN_SORT('auth'),
  },
  {
    key: 'action',
    className: 'data-table-row-actions',
    header: copy.components.endpoints.endpointTable.columns.action,
  },
];

export const LaunchpadEndpointTable: React.VFC = () => {
  const { data: connectionsData, isLoading } = useSavedConnectionsQuery(
    graphQLClient,
    {}
  );

  const endpointItems = React.useMemo(() => {
    const items: Connection[] = [];
    connectionsData?.listConnections?.forEach((conn) => {
      if (!conn) {
        return;
      }
      items.push(conn as Connection);
    });
    return items.sort(alphaSort((item) => item.name));
  }, [connectionsData]);

  const searchFilter = (item: Connection, query: string): boolean => {
    const trimmed = query.trim().toLowerCase();
    return (
      item.name.toLowerCase().includes(trimmed) ||
      item.endpoint.toLowerCase().includes(trimmed)
    );
  };

  const renderRow = (conn: Connection) => (
    <LaunchpadEndpointRow connection={conn} />
  );

  if (isLoading) {
    return (
      <div className="organization-dashboard-loading">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <DataTable
      className="organization-table"
      columns={COLUMNS}
      data={endpointItems}
      getRowKey={(conn) => conn.id}
      renderRow={renderRow}
      searchFilter={searchFilter}
      searchInputTestId={LAUNCHPAD_ENDPOINT_TABLE_SEARCH}
      searchPlaceholder={copy.components.endpoints.endpointTable.search}
      title={copy.components.endpoints.endpointTable.title(
        endpointItems.length
      )}
    />
  );
};
